import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import FranceFlag from "@/public/images/flags/flag_france.svg";
import PortugalFlag from "@/public/images/flags/flag_portugal.svg";
import SpainFlag from "@/public/images/flags/flag_spain.svg";
import UnitedKingdomFlag from "@/public/images/flags/flag_uk.svg";

import {
  ContactFormContentType,
  ContactPageContent,
  ContactSupportContentType,
} from "./schema/contact-page-schema.utils";

export const dropdownDataValues = [
  {
    value: "+44",
    code: "+44",
    optDropdownName: "United Kingdom",
    optDropdownTelCode: "+44",
    optDropdownFlag: {
      src: UnitedKingdomFlag,
      alt: "flag_uk",
      width: 24,
      height: 24,
    },
  },
  {
    value: "+34",
    code: "+34",
    optDropdownName: "Spain",
    optDropdownTelCode: "+34",
    optDropdownFlag: {
      src: SpainFlag,
      alt: "flag_spain",
      width: 24,
      height: 24,
    },
  },
  {
    value: "+33",
    code: "+33",
    optDropdownName: "France",
    optDropdownTelCode: "+33",
    optDropdownFlag: {
      src: FranceFlag,
      alt: "flag_france",
      width: 24,
      height: 24,
    },
  },
  {
    value: "+351",
    code: "+351",
    optDropdownName: "Portugal",
    optDropdownTelCode: "+351",
    optDropdownFlag: {
      src: PortugalFlag,
      alt: "flag_portugal",
      width: 24,
      height: 24,
    },
  },
];

export const emailRegexValidation =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.){1,3}[a-zA-Z]{2,}$/;

export function formCmsData(cmsData: ContactPageContent) {
  const formDataObj = cmsData.contactDetailsArea[0].leftArea[0];

  const formContent: ContactFormContentType = {
    mobileLabel: formDataObj.mobileLabel,
    preferenceDescription: formDataObj.preferenceDescription,
    privacyPolicyDescription: formDataObj.privacyPolicyDescription,
    retainQuoteConsent: formDataObj.retainQuoteConsent ?? "",
    ctaLabelThree: formDataObj.ctaLabelThree,
    ctaLabelOne: formDataObj.ctaLabelOne,
    mobileLabelIsInvalid: formDataObj.mobileLabelIsInvalid,
    title: formDataObj.title,
    mobileLabelIsEmpty: formDataObj.mobileLabelIsEmpty,
    mobileDescription: formDataObj.mobileDescription,
    emailLabel: formDataObj.emailLabel,
    ctaLabelFive: formDataObj.ctaLabelFive,
    ctaLabelTwo: formDataObj.ctaLabelTwo,
    ctaLabelFour: formDataObj.ctaLabelFour,
    subtitle: formDataObj.subtitle,
    emailLabelIsEmpty: formDataObj.emailLabelIsEmpty,
    emailDescription: formDataObj.emailDescription,
    emailLabelIsInvalid: formDataObj.emailLabelIsInvalid,
    ctaLabelOneId: formDataObj.ctaLabelOneId,
    ctaLabelTwoId: formDataObj.ctaLabelTwoId,
    ctaLabelThreeId: formDataObj.ctaLabelThreeId,
    ctaLabelFourId: formDataObj.ctaLabelFourId,
    finishEditingBtnText: formDataObj.finishEditingBtnText,
    cancelEditingBtnText: formDataObj.cancelEditingBtnText,
    usagePage: formDataObj.usagePage,
    backToRenewalsText: formDataObj.backToRenewalsText,
    renewalNonEditInfoBox: formDataObj.renewalNonEditInfoBox,
  };
  return formContent;
}

export function descriptionSectionCmsData(cmsData: ContactPageContent) {
  const descriptionDataObj = cmsData.contactDetailsArea[0].rightArea[0];
  const descriptionContent: ContactSupportContentType = {
    title: descriptionDataObj.title,
    bodyText: descriptionDataObj.bodyText,
    isButtonVisible: descriptionDataObj.isButtonVisible,
    isOpenInNewtab: descriptionDataObj.isOpenInNewtab,
    trustpilot: descriptionDataObj.trustpilot,
    filledStateTitle: descriptionDataObj.filledStateTitle,
    filledStateBodyText: descriptionDataObj.filledStateBodyText,
    ctaLabel: descriptionDataObj.ctaLabel,
    ctaUrl: descriptionDataObj.ctaUrl,
    chatButtonLabel: descriptionDataObj.chatButtonLabel,
  };
  return descriptionContent;
}

// utils/validation-utils.ts
export const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.){1,3}[a-zA-Z]{2,}$/;

export const emailValidation = (
  value: string,
  emailLabelIsEmpty: string,
  emailLabelIsInvalid: string,
  setErrorEmailMessage: (msg: string) => void
): boolean => {
  if (value.trim() === "") {
    setErrorEmailMessage(emailLabelIsEmpty);
    return false;
  }

  if (!emailRegex.test(value.trim())) {
    setErrorEmailMessage(emailLabelIsInvalid);
    return false;
  }

  return true;
};

export const phoneNumberValidation = (
  value: string,
  mobileRegex1: RegExp,
  mobileLabelIsEmpty: string,
  mobileLabelIsInvalid: string,
  setInvalidPhoneMessage: (msg: string) => void
): boolean => {
  if (value.trim() === "") {
    setInvalidPhoneMessage(mobileLabelIsEmpty);
    return false;
  }

  if (!mobileRegex1.test(value.replace(/\s/g, ""))) {
    setInvalidPhoneMessage(mobileLabelIsInvalid);
    return false;
  }

  return true;
};

export const removeCountryCode = (
  phoneNumber: string,
  setTelCountryCode: (code: string) => void
): string => {
  const countryCodeMap: { [key: string]: string } = {
    "+34": "+34",
    "0034": "+34",
    "+351": "+351",
    "00351": "+351",
    "+33": "+33",
    "0033": "+33",
    "+44": "+44",
    "0044": "+44",
  };

  for (const [code, standardCode] of Object.entries(countryCodeMap)) {
    if (phoneNumber.startsWith(code)) {
      setTelCountryCode(standardCode);
      return phoneNumber.replace(code, "").trim();
    }
  }

  return phoneNumber;
};

export const isContactPageShiftFlowExperimentOn = () =>
  localStorage.getItem(OptimizelyExperimentsEnum.QUESTION_ORDER) === "variant";

export const setContactPageShiftFlowValue = (value: string) => {
  localStorage.setItem("contactPageShiftFlow", value);
  document.cookie = `contactPageShiftFlow=${value}; path=/`;
};
